import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { BaseComponent } from '@components/base-component';
import { Profile } from '@common/models/profile.model';
import { BUTTON_BACK } from '@constants/pages-config.constants';
import { StoreDataService } from '@common/services/store-data.service';
import { FormUtilsService } from '@common/services/form-utils.service';
import { PickerData } from '@common/models/picker-option.model';
import { CONTACT_PREFERENCES } from '@constants/contact-preferences.constants';
import { ROOM_NAMES } from '@constants/room-data.constants';
import { ApplianceObj, Room, RoomObj, SelectedAppliance } from '@common/models/rooms.model';
import { ALL_ROOMS_OBJ, APPLIANCES_OBJ } from '@constants/room-data.constants';
import { CLEANING_FREQUENCY } from '@constants/quote-properties.constants';
import { CHECK_PROFILE_STATE_INT_VALUE } from '@constants/common.constants';
import { OrderParameter } from '@common/models/order-type.model';

@Component({
    selector: 'app-summary',
    templateUrl: './summary.component.html',
    styleUrl: './summary.component.scss',
    standalone: false,
})
export class SummaryComponent extends BaseComponent implements OnInit, OnDestroy {
    override progressBarStatus = {
        progress: 4,
        completedStep: 3,
    };
    override navigationSubscription!: Subscription;
    override navigationRoutes = {
        nextRoute: this.pagesUrls[this.pages.BOOKING_COMPLETED],
        backRoute: this.pagesUrls[this.pages.SCHEDULE],
    };
    override isNextButtonDisabled = true;
    override isNextButtonActive = false;
    nextButtonText = 'Let`s get in touch!';
    profileSubscription!: Subscription;
    roomNames = ROOM_NAMES;

    firstName!: string;
    lastName!: string;
    email!: string;
    phone!: string;
    petsNumber!: number;
    levelsNumberText!: string;
    levelsNumberMap: { [key: number]: string } = {
        '1': 'One',
        '2': 'Two',
        '3': 'Three',
        '4': 'Four',
    };
    cleaningFrequency = CLEANING_FREQUENCY;
    frequencyTitleMap: { [key: string]: string } = {
        [this.cleaningFrequency.WEEKLY]: 'Weekly',
        [this.cleaningFrequency.BI_WEEKLY]: 'Every other week',
        [this.cleaningFrequency.MONTHLY]: 'Every four weeks',
        [this.cleaningFrequency.ONE_TIME]: 'One-time',
    };

    squareFootage!: string;
    address!: string;
    allRooms!: Room[];
    selectedRoomsObj!: RoomObj[];
    selectedAppliances: SelectedAppliance[] = [];
    selectedAppliancesObj!: ApplianceObj[];

    contactPrefData: Array<PickerData> = [
        { label: 'Phone', value: CONTACT_PREFERENCES.PHONE },
        { label: 'Text', value: CONTACT_PREFERENCES.TEXT },
        { label: 'Email', value: CONTACT_PREFERENCES.EMAIL },
        { label: 'No preference', value: CONTACT_PREFERENCES.NO_PREFERENCE },
    ];
    contactPrefSelectedValues!: string[];
    specialContactPrefValue = CONTACT_PREFERENCES.NO_PREFERENCE;
    regularContactPrefValues = [
        CONTACT_PREFERENCES.PHONE,
        CONTACT_PREFERENCES.TEXT,
        CONTACT_PREFERENCES.EMAIL,
    ];
    isEditContactsMode = false;
    selectedFrequency!: string;
    frequencyTitle!: string;
    isOneTimeClean = true;
    firstTimePrice!: string | null;
    recurringPrice!: string | null;
    isDetailedMode = false;
    contactForm: FormGroup = new FormGroup({});
    notesForm: FormGroup = new FormGroup({});
    notes!: string;
    country!: string;
    selectedDate!: Date;
    firstCleaningDateToString!: string;
    locationCode!: number;

    profileState!: Profile;
    checkProfileStateInterval!: ReturnType<typeof setInterval>;
    websiteOfferName!: string;

    constructor(
        private storeDataService: StoreDataService,
        private formBuilder: FormBuilder,
        private formUtilsService: FormUtilsService
    ) {
        super();
        this.checkProfileStateInterval = setInterval(() => {
            const sanitizedProfile = this.formUtilsService.sanitizeProfileData(this.profileState);
            this.storeDataService.updateProfileData(sanitizedProfile);
        }, CHECK_PROFILE_STATE_INT_VALUE);
    }

    private focusedField: string | null = null;

    public onFieldFocus(fieldName: string): void {
        this.focusedField = fieldName;
    }

    public onFieldBlur(): void {
        this.focusedField = null;
    }

    public get isFirstNameInvalid(): boolean {
        const control = this.contactForm.get('firstName');
        return !!(
            control &&
            control.touched &&
            control.invalid &&
            this.focusedField !== 'firstName'
        );
    }

    public get isLastNameInvalid(): boolean {
        const control = this.contactForm.get('lastName');
        return !!(
            control &&
            control.touched &&
            control.invalid &&
            this.focusedField !== 'lastName'
        );
    }

    public get isEmailInvalid(): boolean {
        const control = this.contactForm.get('email');
        return !!(control && control.touched && !control.valid && this.focusedField !== 'email');
    }

    public get isPhoneInvalid(): boolean {
        const control = this.contactForm.get('phone');
        return !!(control && control.touched && !control.valid && this.focusedField !== 'phone');
    }

    public get isNotesInvalid(): boolean {
        const control = this.notesForm.get('notes');
        return !!(control && control.invalid);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        super.setBrowserBackRoute(this.navigationRoutes.backRoute);
        this.navigationService.setNextButtonActive(this.isNextButtonActive);
        const nextButtonSubscription = this.navigationService
            .getNavigationButtonClicked()
            .subscribe((res) => {
                if (this.isNavigationButtonClicked(res.button) && !res.allowNavigation) {
                    this.storeCurrentData(res.button);
                }
            });

        this.buildContactForm();
        this.handleContactForm();
        this.profileSubscription = this.storeDataService
            .profileObserver()
            .subscribe((profile: Profile) => {
                if (profile.profileId && !profile.skipUpdate) this.setupDefaultOptions(profile);
                delete profile.skipUpdate;
            });
        this.addSubscription(this.profileSubscription);
        this.addSubscription(nextButtonSubscription);
    }

    setupDefaultOptions(profile: Profile) {
        this.profileState = JSON.parse(JSON.stringify(profile));
        if (profile.firstName) {
            this.firstName = profile.firstName;
            this.contactForm.get('firstName')?.setValue(profile.firstName);
            this.contactForm.controls['firstName']?.markAsTouched();
        }
        if (profile.lastName) {
            this.lastName = profile.lastName;
            this.contactForm.get('lastName')?.setValue(profile.lastName);
            this.contactForm.controls['lastName']?.markAsTouched();
        }
        if (profile.email) {
            this.email = profile.email;
            this.contactForm.get('email')?.setValue(profile.email);
            this.contactForm.controls['email']?.markAsTouched();
        }
        if (profile.phone) {
            this.phone = this.phoneFormatter(profile.phone);
            this.contactForm.get('phone')?.setValue(profile.phone);
            this.contactForm.controls['phone']?.markAsTouched();
        }
        if (profile.petsNumber) {
            this.petsNumber = profile.petsNumber;
        }
        if (profile.levels) {
            this.levelsNumberText = this.levelsNumberMap[profile.levels];
        }
        if (profile.square) {
            this.squareFootage = profile.square.toLocaleString('en-US');
        }
        if (profile.contactPreference) {
            this.contactPrefSelectedValues = profile.contactPreference as string[];
        }
        if (profile.cleaningFrequency) {
            this.selectedFrequency = profile.cleaningFrequency;
            this.frequencyTitle = this.frequencyTitleMap[this.selectedFrequency];
        }
        if (profile.appliances?.length) {
            this.selectedAppliances = profile.appliances;
            this.selectedAppliancesObj = profile.appliances.map((appliance: any) => ({
                ...APPLIANCES_OBJ[appliance.type],
                type: appliance.type,
            }));
        }
        if (profile.locationCode) {
            this.locationCode = profile.locationCode;
        }
        if (profile.rooms?.length && profile.otherRooms?.length) {
            const roomOrder: string[] = [
                this.roomNames.BEDROOM,
                this.roomNames.BATHROOM,
                this.roomNames.HALF_BATH,
            ];
            const sortedRooms = profile.rooms.sort((a, b) => {
                const indexA = roomOrder.indexOf(a.roomName.toLowerCase());
                const indexB = roomOrder.indexOf(b.roomName.toLowerCase());
                return (
                    (indexA !== -1 ? indexA : roomOrder.length) -
                    (indexB !== -1 ? indexB : roomOrder.length)
                );
            });
            this.allRooms = [...sortedRooms, ...profile.otherRooms];
            this.checkIsDetailedMode();
            this.selectedRoomsObj = this.mapSelectedRooms();
        }
        if (profile.concerns) {
            this.notes = profile.concerns;
        }
        if (profile.firstCleanPrice) {
            this.firstTimePrice = profile.firstCleanPrice;
        }
        if (profile.websiteOfferName) {
            this.websiteOfferName = profile.websiteOfferName;
        }
        if (profile.recurringCleanPrice) {
            this.recurringPrice = profile.recurringCleanPrice;
            this.isOneTimeClean = false;
        }
        this.country = profile.country ? profile.country : 'US';
        if (profile.selectedStartDate) {
            this.selectedDate = new Date(profile.selectedStartDate + 'T00:00:00');
            this.firstCleaningDateToString = this.formatDateToString(this.selectedDate);
            this.nextButtonText = 'Schedule now';
        }
        this.navigationService.setNextButtonLabel(this.nextButtonText);

        const { address, postalCode, city, state } = this.storeDataService.profileData;
        const suite =
            this.storeDataService.profileData.suite &&
            this.storeDataService.profileData.suite !== 'null'
                ? ', ' + this.storeDataService.profileData.suite
                : '';
        this.address = `${address}${suite}, ${city}, ${state} ${postalCode}`;
        this.buildNotesForm();
        this.handleNotesForm();
        this.handleNavigation();
    }

    formatDateToString(date: Date): string {
        const formatter = new Intl.DateTimeFormat('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
        return formatter.format(date);
    }

    checkIsDetailedMode() {
        this.isDetailedMode = this.allRooms.every((room) => room.isDetailed);
    }

    mapSelectedRooms() {
        return this.allRooms
            .filter((room) => ALL_ROOMS_OBJ.hasOwnProperty(room.roomName) && room.isSelected)
            .map((otherRoom) => ({
                ...ALL_ROOMS_OBJ[otherRoom.roomName],
                isSelected: otherRoom.isSelected,
                isDetailed: otherRoom.isDetailed,
                id: otherRoom.id,
            }));
    }

    buildContactForm(): void {
        this.contactForm = this.formUtilsService.buildContactForm();
    }

    handleContactForm(): void {
        const contactFormSubscription = this.contactForm.valueChanges.subscribe(() => {
            this.handleNavigation();
        });
        this.addSubscription(contactFormSubscription);
    }

    buildNotesForm(): void {
        this.notesForm = this.formBuilder.group({
            notes: new FormControl(this.notes, [
                Validators.maxLength(750),
                Validators.pattern(
                    /^[a-zA-Z0-9\s\r\n\p{Emoji}.,!?:;()'"“”‘’&@#%*_\-+=/|~\[\]{}]*$/u
                ),
            ]),
        });
    }

    handleNotesForm(): void {
        const notesFormSubscription = this.notesForm.valueChanges.subscribe((value) => {
            this.notes = value.notes;
            if (this.notesForm.valid) {
                this.profileState.concerns = this.notes;
            }
            this.handleNavigation();
        });
        this.addSubscription(notesFormSubscription);
    }

    onNameInput(event: Event): void {
        this.formUtilsService.handleNameInput(event, this.contactForm);
    }

    onEmailInput(event: Event): void {
        this.formUtilsService.handleEmailInput(event, this.contactForm);
    }

    onContactFormSubmit() {
        this.formUtilsService.trimFormValues(this.contactForm);

        if (this.contactForm.valid) {
            this.isEditContactsMode = false;
            const contactFormValues = this.formUtilsService.sanitizeContactFormValues(
                this.contactForm.value
            );

            this.storeDataService
                .storeCurrentData({
                    ...contactFormValues,
                })
                .subscribe();
        }
    }

    editContactsModeOn() {
        this.isEditContactsMode = true;
    }

    handleEditKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.editContactsModeOn();
        }
    }

    handleQuoteKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.navigateToQuotePage();
        }
    }

    navigateToQuotePage() {
        if (this.checkProfileStateInterval) {
            clearInterval(this.checkProfileStateInterval);
        }

        this.profileState = this.formUtilsService.sanitizeProfileData(this.profileState);

        this.storeDataService
            .storeCurrentData({
                ...this.profileState,
                url: this.navigationRoutes.backRoute,
            })
            .subscribe(() => {
                this.router.navigate([this.pagesUrls[this.pages.QUOTE]], {
                    queryParamsHandling: 'preserve',
                });
            });
    }

    onContactPrefSelected(event: (string | number)[]) {
        this.contactPrefSelectedValues = event as string[];
        this.profileState.contactPreference = this.contactPrefSelectedValues;
        this.handleNavigation();
    }

    handleNavigation() {
        if (this.contactPrefSelectedValues?.length && this.notesForm.valid && this.contactForm.valid) {
            this.isNextButtonDisabled = false;
            this.isNextButtonActive = true;
            this.navigationService.setNextButtonDisabled(this.isNextButtonDisabled);
            this.navigationService.setNextButtonActive(this.isNextButtonActive);
        } else {
            this.isNextButtonDisabled = true;
            this.navigationService.setNextButtonDisabled(this.isNextButtonDisabled);
        }
    }

    phoneFormatter(phone: string): string {
        return `${phone.slice(0, 3)}.${phone.slice(3, 6)}.${phone.slice(6, 10)}`;
    }

    storeCurrentData(button: string) {
        if (this.checkProfileStateInterval) {
            clearInterval(this.checkProfileStateInterval);
        }

        this.profileState = this.formUtilsService.sanitizeProfileData(this.profileState);

        const isBackButton = button === BUTTON_BACK;
        if (!isBackButton) {
            if (this.notes) {
                this.googleAnalyticsService.event('notes_added', 'notes', 'Specific needs');
            }

            if (this.contactPrefSelectedValues?.length) {
                const contactPrefs = this.contactPrefSelectedValues.join(', ');
                this.googleAnalyticsService.event(
                    'contact_preferences_checked',
                    'contact_preference',
                    contactPrefs
                );
            }
            this.googleAnalyticsService.event('agree_terms', 'terms_policy', 'Terms Of Use');
            this.googleAnalyticsService.event('agree_policy', 'terms_policy', 'Privacy Policy');
            this.storeDataService
                .storeCurrentData({
                    ...this.profileState,
                    url: this.navigationRoutes.nextRoute,
                })
                .subscribe(() => {
                    let param: OrderParameter = '';
                    if (!this.profileState.selectedStartDate) {
                        param = 'CustomizeDate';
                    }
                    const gaLabel = param === 'CustomizeDate' ? 'Customize Date' : 'Schedule Now';
                    const gaEvent = param === 'CustomizeDate' ? 'order_completed_no_date' : 'order_completed';
                    this.googleAnalyticsService.event(
                        gaEvent,
                        'order_completed',
                        gaLabel
                    );
                    this.storeDataService.sendOrder(param).subscribe(() => {
                        this.navigationService.setNavigationButtonClicked({
                            button: button,
                            allowNavigation: true,
                        });
                    });
                });
        } else {
            this.storeDataService
                .storeCurrentData({
                    ...this.profileState,
                    url: this.navigationRoutes.backRoute,
                })
                .subscribe(() => {
                    this.navigationService.setNavigationButtonClicked({
                        button: button,
                        allowNavigation: true,
                    });
                });
        }
    }

    override ngOnDestroy(): void {
        if (this.checkProfileStateInterval) {
            clearInterval(this.checkProfileStateInterval);
        }
        super.ngOnDestroy();
    }
}
